import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const GalleryLightbox = ({
  open,
  onOpenChange,
  images,
  initialIndex = 0,
  title = "",
}) => {
  const [index, setIndex] = useState(initialIndex);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[90] flex flex-col text-[#F6F4EF] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 focus:outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {title || "Photo gallery"}
          </DialogPrimitive.Title>

          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4 md:px-8 md:py-6">
            <p
              data-testid="lightbox-counter"
              className="text-xs uppercase tracking-[0.2em] text-[#F6F4EF]/70"
            >
              {index + 1} / {images.length}
            </p>
            <DialogPrimitive.Close
              data-testid="lightbox-close"
              aria-label="Close gallery"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-[#F6F4EF] transition-colors hover:bg-white/10 focus:outline-none"
            >
              <X size={16} />
            </DialogPrimitive.Close>
          </div>

          {/* Main image */}
          <div
            className="relative flex flex-1 items-center justify-center px-4 md:px-16"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              key={index}
              src={images[index]}
              alt=""
              data-testid="lightbox-image"
              className="max-h-[75vh] max-w-full object-contain reveal-up"
            />

            <button
              type="button"
              data-testid="lightbox-prev"
              onClick={prev}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/40 backdrop-blur transition-colors hover:bg-black/60 md:left-8 md:h-12 md:w-12"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              onClick={next}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/40 backdrop-blur transition-colors hover:bg-black/60 md:right-8 md:h-12 md:w-12"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="no-scrollbar mx-auto flex w-full max-w-[900px] gap-2 overflow-x-auto px-5 py-4 md:gap-3 md:px-8 md:py-6">
            {images.map((src, i) => (
              <button
                key={i}
                type="button"
                data-testid={`lightbox-thumb-${i}`}
                onClick={() => setIndex(i)}
                className={`relative h-14 w-20 shrink-0 overflow-hidden transition-opacity duration-300 md:h-16 md:w-24 ${
                  i === index
                    ? "opacity-100 ring-2 ring-primary ring-offset-2 ring-offset-black"
                    : "opacity-50 hover:opacity-80"
                }`}
              >
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default GalleryLightbox;
